import { GameManager, DeckPhase, MatchPhase } from './game';
import { PlayerState, MatchResult, CustomGameConfig, DEFAULT_CUSTOM_CONFIG } from './types';
import {
  render,
  renderLobby,
  renderDeckPhase,
  renderMatchPhase,
  renderRoundResult,
  renderFinalsIntro,
  renderGameEnd,
  renderEliminationScreen,
  renderSpectatorView,
  renderRoundTransition,
  renderMatchPreview,
  renderFinalistsAnnouncement,
  updateReadyStatus,
  renderStarterDeckSelection,
  renderTutorialScreen,
  renderQuickHelp,
  isTutorialCompleted,
  resetTutorial,
  renderSpectatorMode,
  stopAutoRefresh,
  resetSpectatorState,
  renderCustomRulesScreen,
  resetToDefault,
  renderMatchBattle,
  cleanupMatchBattle,
  initMultiplayerMatch,
} from './ui';
import { getDiscordClient, DiscordUser, getMultiplayerManager } from './sdk';

interface AppState {
  isDiscordReady: boolean;
  currentUser: { id: string; name: string; avatar?: string } | null;
  game: GameManager | null;
  currentDeckPhase: DeckPhase | null;
  isMultiplayer: boolean;
  showingHelp: boolean;
  customConfig: CustomGameConfig;
}

const state: AppState = {
  isDiscordReady: false,
  currentUser: null,
  game: null,
  currentDeckPhase: null,
  isMultiplayer: false,
  showingHelp: false,
  customConfig: { ...DEFAULT_CUSTOM_CONFIG },
};

// ロビー表示
function showLobby(): void {
  if (!state.game || !state.currentUser) return;

  renderLobby(state.game, state.currentUser.id, {
    onStart: startGame,
    onHelp: () => {
      showQuickHelp(() => {
        showLobby();
      });
    },
    onTutorial: () => {
      // チュートリアルリセットして表示
      resetTutorial();
      showTutorial();
    },
    onCustomRules: () => {
      showCustomRulesScreen();
    },
  });
}

// カスタムルール画面
function showCustomRulesScreen(): void {
  renderCustomRulesScreen({
    onConfirm: (config) => {
      state.customConfig = config;
      // GameManagerに設定を適用 (CustomGameConfigから標準GameConfigへ変換)
      if (state.game) {
        state.game.setConfig({
          qualifyingRounds: config.qualifyingRounds,
          maxPlayers: config.playerCount,
          tierDraws: config.tierDraws,
        });
      }
      showLobby();
    },
    onCancel: () => {
      resetToDefault();
      showLobby();
    },
  });
}

// ゲーム開始
async function startGame(): Promise<void> {
  if (!state.game) return;

  state.game.on((event) => {
    console.log('GameEvent:', event);
    handleGameEvent(event);
  });

  // マルチプレイ判定: Humanプレイヤーが2人以上の場合のみ
  const humanCount = state.game.getState().players.filter(p => p.info.type === 'human').length;
  const isRealMultiplayer = state.isMultiplayer && humanCount >= 2;
  
  console.log('[Game] startGame: isMultiplayer=', state.isMultiplayer, 'humanCount=', humanCount, 'isRealMultiplayer=', isRealMultiplayer);

  // 実際のマルチプレイ時のみBot追加をスキップ (ホストがマッチフェーズ開始時に行う)
  state.game.startGame(isRealMultiplayer);
  
  // フラグリセット
  starterSelectionCompleted = false;
  myStarterSelectionNotified = false;
  
  // マルチプレイ時はサーバーのready状態をリセット
  if (isRealMultiplayer) {
    const multiplayer = getMultiplayerManager();
    multiplayer.resetReadyState();
    await multiplayer.resetServerReadyState(); // 内部でホストチェックする
  }
  
  // スターター選択フェーズへ
  showStarterSelection();
}

// スターターデッキ選択表示
function showStarterSelection(): void {
  if (!state.game || !state.currentUser) return;

  const player = state.game.getPlayer(state.currentUser.id);
  if (!player) return;

  const cardPool = state.game.getCardPool();

  renderStarterDeckSelection(state.game, player, cardPool, {
    onSelect: async (deck) => {
      // スターターデッキを設定
      state.game?.setStarterDeck(state.currentUser!.id, deck);
      
      // マルチプレイ時はサーバーに通知
      if (state.isMultiplayer) {
        console.log('[Game] Notifying starter selection complete');
        const multiplayer = getMultiplayerManager();
        
        // フェーズがstarterSelectになるまで待機 (ホストのフェーズ変更を待つ)
        // タイムアウトを延長し、タイムアウトしても続行する
        const phaseReady = await waitForPhase('starterSelect', 15000);
        if (!phaseReady) {
          console.log('[Game] Phase wait timed out, continuing anyway');
        }
        
        // notifyReadyを完了させてから次へ進む
        await multiplayer.notifyReady(true);
        myStarterSelectionNotified = true;  // 自分がready通知を送った
        console.log('[Game] Starter selection ready notification sent');
      } else {
        myStarterSelectionNotified = true;
      }
      
      // 全員準備完了を待って次フェーズへ
      checkStarterSelectionComplete();
    },
    onSkip: async () => {
      // ランダムデッキで続行
      const randomDeck = cardPool.generateStarterDeck();
      state.game?.setStarterDeck(state.currentUser!.id, randomDeck);
      
      // マルチプレイ時はサーバーに通知
      if (state.isMultiplayer) {
        console.log('[Game] Notifying starter selection complete (skip)');
        const multiplayer = getMultiplayerManager();
        
        // フェーズがstarterSelectになるまで待機 (ホストのフェーズ変更を待つ)
        // タイムアウトを延長し、タイムアウトしても続行する
        const phaseReady = await waitForPhase('starterSelect', 15000);
        if (!phaseReady) {
          console.log('[Game] Phase wait timed out, continuing anyway');
        }
        
        // notifyReadyを完了させてから次へ進む
        await multiplayer.notifyReady(true);
        myStarterSelectionNotified = true;  // 自分がready通知を送った
        console.log('[Game] Starter selection ready notification sent');
      } else {
        myStarterSelectionNotified = true;
      }
      
      checkStarterSelectionComplete();
    },
  });
}

// スターター選択完了チェック
let starterSelectionCompleted = false;
let myStarterSelectionNotified = false;  // 自分がready通知を送ったかどうか

// 指定フェーズになるまで待機
async function waitForPhase(targetPhase: string, maxWaitMs: number = 5000): Promise<boolean> {
  const multiplayer = getMultiplayerManager();
  const checkIntervalMs = 500;  // 500msに延長(API節約)
  const startTime = Date.now();
  
  console.log('[Game] Waiting for phase:', targetPhase, 'max wait:', maxWaitMs);
  
  while (Date.now() - startTime < maxWaitMs) {
    const roomState = await multiplayer.getRoomState();
    const currentPhase = roomState?.phase || 'unknown';
    
    if (currentPhase === targetPhase) {
      console.log('[Game] Phase confirmed:', targetPhase);
      return true;
    }
    
    // 進捗ログ (5秒ごと)
    const elapsed = Date.now() - startTime;
    if (elapsed > 0 && elapsed % 5000 < checkIntervalMs) {
      console.log('[Game] Still waiting for phase:', targetPhase, 'current:', currentPhase, 'elapsed:', elapsed);
    }
    
    await new Promise(resolve => setTimeout(resolve, checkIntervalMs));
  }
  
  console.warn('[Game] Timeout waiting for phase:', targetPhase);
  return false;
}

// サーバーのready状態がリセットされるまで待機
async function waitForServerReadyReset(): Promise<void> {
  const multiplayer = getMultiplayerManager();
  const maxWaitMs = 5000; // 最大5秒待機
  const checkIntervalMs = 300;
  const startTime = Date.now();
  
  // 待機中の表示
  render(`
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: #1a1a2e;
      color: #fff;
    ">
      <div style="font-size: 3rem; margin-bottom: 20px;">⏳</div>
      <h2 style="color: #00fff5; margin-bottom: 10px;">次のフェーズを準備中...</h2>
      <p style="color: #aaa;">しばらくお待ちください</p>
    </div>
  `);
  
  while (Date.now() - startTime < maxWaitMs) {
    const roomState = await multiplayer.getRoomState();
    
    if (roomState) {
      // 全員のisReadyがfalseになっているか確認 (Botは除く)
      const humanPlayers = roomState.players.filter(p => {
        const gamePlayer = state.game?.getPlayer(p.id);
        return gamePlayer?.info.type !== 'bot';
      });
      
      const allReset = humanPlayers.every(p => p.isReady === false);
      
      if (allReset) {
        console.log('[Game] Server ready state reset confirmed');
        return;
      }
    }
    
    // 少し待ってから再チェック
    await new Promise(resolve => setTimeout(resolve, checkIntervalMs));
  }
  
  // タイムアウト - 強制的に進む
  console.warn('[Game] Timeout waiting for server ready reset, proceeding anyway');
}

async function checkStarterSelectionComplete(): Promise<void> {
  if (!state.game) return;
  
  // 既に完了済みなら何もしない
  if (starterSelectionCompleted) return;
  
  // マルチプレイ時は自分がready通知を送っていない場合はスキップ
  if (state.isMultiplayer && !myStarterSelectionNotified) {
    console.log('[Game] Skipping starter selection check - not yet notified');
    return;
  }

  // マルチプレイ時はサーバーから最新状態を取得
  if (state.isMultiplayer) {
    const multiplayer = getMultiplayerManager();
    
    // 自分のready通知がサーバーに反映されるまで少し待つ
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const roomState = await multiplayer.getRoomState();
    
    if (roomState) {
      console.log('[Game] Room state phase:', roomState.phase, 'players ready:', 
        roomState.players.filter(p => p.isReady).length, '/', roomState.players.length);
      
      // フェーズが既にdeckPhaseに進んでいる場合は強制的に完了
      if (roomState.phase === 'deckPhase' || roomState.phase === 'matchPhase') {
        console.log('[Game] Phase already advanced to', roomState.phase, '- forcing completion');
        starterSelectionCompleted = true;
        state.game.completeStarterSelection();
        return;
      }
      
      // サーバーのready状態をGameManagerに反映
      for (const p of roomState.players) {
        if (p.isReady) {
          state.game.setPlayerReady(p.id, true);
        }
      }
    }
  }

  // 全員準備完了か確認
  const readyStates = state.game.getReadyStates();
  const allReady = readyStates.every((rs) => rs.isReady);

  if (allReady) {
    console.log('[Game] All players ready - completing starter selection');
    starterSelectionCompleted = true;
    state.game.completeStarterSelection();
  } else {
    // 待機画面
    render(`
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: #1a1a2e;
        color: #fff;
      ">
        <div style="font-size: 3rem; margin-bottom: 20px;">🃏</div>
        <h2 style="color: #ffd700; margin-bottom: 10px;">デッキ選択完了!</h2>
        <p style="color: #aaa;">他のプレイヤーを待っています...</p>
        <div style="margin-top: 20px; display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;">
          ${readyStates.map((rs) => `
            <span style="
              padding: 5px 12px;
              border-radius: 20px;
              background: ${rs.isReady ? 'rgba(46, 204, 113, 0.2)' : 'transparent'};
              border: 1px solid ${rs.isReady ? '#2ecc71' : '#444'};
              color: ${rs.isReady ? '#2ecc71' : '#888'};
            ">
              ${rs.isBot ? '🤖' : '👤'} ${rs.name} ${rs.isReady ? '✓' : '⏳'}
            </span>
          `).join('')}
        </div>
      </div>
    `);

    // 1.5秒後に再チェック (API節約)
    setTimeout(() => {
      checkStarterSelectionComplete();
    }, 1500);
  }
}

// イベントハンドラ
function handleGameEvent(event: { type: string; data: unknown }): void {
  switch (event.type) {
    case 'roundStarted':
      // マルチプレイ時はready状態をリセット
      if (state.isMultiplayer) {
        const multiplayer = getMultiplayerManager();
        multiplayer.resetReadyState();
        
        // GameManagerのready状態もリセット (全クライアント共通)
        state.game?.resetAllReadyStates();
        
        // ホストならサーバーのready状態もリセット
        if (multiplayer.getIsHost()) {
          const roundData = event.data as { roundNumber?: number };
          multiplayer.updatePhase('deckPhase', roundData.roundNumber);
        }
        
        // サーバーのready状態がリセットされるまで待機してからUI表示
        waitForServerReadyReset().then(() => {
          showDeckPhase();
        });
      } else {
        showDeckPhase();
      }
      break;
    case 'playerReady':
    case 'allPlayersReady':
      // 準備状況を更新
      if (state.game) {
        updateReadyStatus(state.game);
      }
      break;
    case 'phaseChanged':
      if ((event.data as { phase: string }).phase === 'matchPhase') {
        showBattleScreen();
      }
      break;
    case 'roundEnded':
      // 敗退チェック
      if (checkPlayerEliminated()) {
        showEliminationScreen();
      } else {
        showRoundResult();
      }
      break;
    case 'playerEliminated':
      // 自分が敗退した場合
      const eliminatedData = event.data as { playerId: string };
      if (eliminatedData.playerId === state.currentUser?.id) {
        showEliminationScreen();
      }
      break;
    case 'finalsStarted':
      showFinals();
      break;
    case 'gameEnded':
      showGameEnd();
      break;
  }
}

// プレイヤーの敗退チェック
function checkPlayerEliminated(): boolean {
  if (!state.game || !state.currentUser) return false;
  
  const player = state.game.getPlayer(state.currentUser.id);
  return player?.isEliminated ?? false;
}

// 敗退画面表示
function showEliminationScreen(): void {
  if (!state.game || !state.currentUser) return;
  
  const player = state.game.getPlayer(state.currentUser.id);
  if (!player) return;
  
  const ranking = state.game.getRanking();
  const myRank = ranking.find((r) => r.player.info.id === state.currentUser?.id)?.rank ?? 0;
  const totalPlayers = state.game.getState().players.length;
  
  renderEliminationScreen(player, myRank, totalPlayers, {
    onSpectate: () => {
      showSpectatorMode();
    },
    onExit: () => {
      init();
    },
  });
}

// 観戦モード
function showSpectatorMode(): void {
  if (!state.game) return;
  
  // 状態リセット
  resetSpectatorState();
  
  // 新しい高機能観戦モード
  renderSpectatorMode(state.game, {
    onExit: () => {
      stopAutoRefresh();
      init();
    },
  });
  
  // ゲーム終了監視
  const gameEndCheck = setInterval(() => {
    if (!state.game) {
      clearInterval(gameEndCheck);
      stopAutoRefresh();
      return;
    }
    
    const gamePhase = state.game.getState().phase;
    if (gamePhase === 'gameEnd') {
      clearInterval(gameEndCheck);
      stopAutoRefresh();
      showGameEnd();
    }
  }, 2000);
}

// デッキフェイズ表示
function showDeckPhase(): void {
  if (!state.game || !state.currentUser) return;

  const player = state.game.getPlayer(state.currentUser.id);
  if (!player) return;

  const round = state.game.getState().currentRound?.roundNumber ?? 1;
  const tierDraws = state.game.getState().config.tierDraws;
  state.currentDeckPhase = new DeckPhase(state.game.getCardPool(), round, tierDraws);

  renderDeckPhase(state.game, player, state.currentDeckPhase, {
    onComplete: async () => {
      // 個別プレイヤーの準備完了を通知
      if (state.game && state.currentUser) {
        state.game.playerCompleteDeckPhase(state.currentUser.id);
        
        // マルチプレイ時はサーバーに通知
        if (state.isMultiplayer) {
          const multiplayer = getMultiplayerManager();
          const result = await multiplayer.notifyDeckPhaseComplete();
          console.log('[Game] Deck phase complete notification sent');
        }
        
        // 全員準備完了を待ってマッチフェイズへ
        state.game.completeDeckPhaseWhenReady();
      }
    },
  });
}

// プレイヤーのストリーク計算
function calculateStreak(history: { result: 'win' | 'lose' }[]): number {
  if (history.length === 0) return 0;
  
  let streak = 0;
  const lastResult = history[history.length - 1].result;
  
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].result === lastResult) {
      streak += lastResult === 'win' ? 1 : -1;
    } else {
      break;
    }
  }
  
  return streak;
}

// バトル画面表示 (リアルタイム進行)
async function showBattleScreen(): Promise<void> {
  if (!state.game || !state.currentUser) return;

  // マルチプレイ判定: Humanプレイヤーが2人以上の場合のみ
  const humanCount = state.game.getState().players.filter(p => p.info.type === 'human').length;
  const isRealMultiplayer = state.isMultiplayer && humanCount >= 2;

  // マルチプレイ時はペアリング同期が必要
  if (isRealMultiplayer) {
    await showMultiplayerBattleScreen();
    return;
  }

  // シングルプレイ: 従来の処理
  const pairings = state.game.getMatchPairings();
  
  console.log('[Game] Match pairings:', pairings.map(p => ({
    p1: p.player1.info.name + '(' + p.player1.info.type + ')',
    p2: p.player2.info.name + '(' + p.player2.info.type + ')',
  })));
  
  const myPairing = pairings.find(
    (p) => p.player1.info.id === state.currentUser?.id || p.player2.info.id === state.currentUser?.id
  );

  if (!myPairing) {
    runAllBotMatches();
    state.game?.completeMatches();
    return;
  }

  const currentRound = state.game.getState().currentRound;
  if (!currentRound) return;

  const opponentId = myPairing.player1.info.id === state.currentUser.id 
    ? myPairing.player2.info.id 
    : myPairing.player1.info.id;
  const opponent = state.game.getPlayer(opponentId);
  const opponentIsBot = opponent?.info.type === 'bot';

  const matchPhase = state.game.createMatchPhase(
    myPairing.player1.info.id,
    myPairing.player2.info.id,
    myPairing.firstPlayerId
  );

  if (!matchPhase) return;

  renderMatchBattle(matchPhase, state.currentUser.id, opponentId, opponentIsBot, (result) => {
    cleanupMatchBattle();
    
    const matchResult = matchPhase.getResult();
    if (matchResult) {
      state.game?.registerMatchResult(matchResult);
    }
    
    runAllBotMatches();
    state.game?.completeMatches();
  });
}

// マルチプレイ用バトル画面
async function showMultiplayerBattleScreen(): Promise<void> {
  if (!state.game || !state.currentUser) return;

  const multiplayer = getMultiplayerManager();
  const isHost = multiplayer.getIsHost();

  // 待機画面
  render(`
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: #1a1a2e;
      color: #fff;
    ">
      <div style="font-size: 3rem; margin-bottom: 20px;">⚔️</div>
      <h2 style="color: #ff6b6b; margin-bottom: 10px;">対戦準備中...</h2>
      <p style="color: #aaa;">${isHost ? 'マッチを準備しています' : 'ホストからのマッチ情報を待っています'}</p>
    </div>
  `);

  let pairings: { player1: PlayerState; player2: PlayerState; firstPlayerId: string }[];
  
  if (isHost) {
    // ホスト: Bot追加 → ペアリング生成 → サーバーに保存
    pairings = await setupPairingsAsHost();
  } else {
    // 非ホスト: サーバーからペアリング情報を取得 → ローカルにBot追加
    pairings = await receivePairingsFromHost();
  }

  console.log('[Game] Final pairings:', pairings.map(p => ({
    p1: p.player1.info.name + '(' + p.player1.info.type + ')',
    p2: p.player2.info.name + '(' + p.player2.info.type + ')',
  })));

  // 自分のマッチを探す
  const myPairingIndex = pairings.findIndex(
    (p) => p.player1.info.id === state.currentUser?.id || p.player2.info.id === state.currentUser?.id
  );
  const myPairing = myPairingIndex >= 0 ? pairings[myPairingIndex] : undefined;

  console.log('[Game] My pairing:', myPairing ? {
    p1: myPairing.player1.info.name + '(' + myPairing.player1.info.type + ')',
    p2: myPairing.player2.info.name + '(' + myPairing.player2.info.type + ')',
    matchIndex: myPairingIndex,
  } : 'none (Bye)');

  if (!myPairing) {
    // Bye: マッチなし
    runAllBotMatches();
    await waitForAllMatchesAndComplete();
    return;
  }

  const currentRound = state.game.getState().currentRound;
  if (!currentRound) return;

  const opponentId = myPairing.player1.info.id === state.currentUser.id 
    ? myPairing.player2.info.id 
    : myPairing.player1.info.id;
  const opponent = state.game.getPlayer(opponentId);
  const opponentIsBot = opponent?.info.type === 'bot';

  // PVP (対人戦)
  if (!opponentIsBot) {
    await showMultiplayerBattle(myPairing, currentRound.roundNumber, opponentIsBot, myPairingIndex);
    return;
  }

  // PVE (Bot戦) - ローカルで処理
  const matchPhase = state.game.createMatchPhase(
    myPairing.player1.info.id,
    myPairing.player2.info.id,
    myPairing.firstPlayerId
  );

  if (!matchPhase) return;

  renderMatchBattle(matchPhase, state.currentUser.id, opponentId, opponentIsBot, async (result) => {
    cleanupMatchBattle();
    
    const matchResult = matchPhase.getResult();
    if (matchResult) {
      state.game?.registerMatchResult(matchResult);
    }
    
    runAllBotMatches();
    await waitForAllMatchesAndComplete();
  });
}

// ホスト: ペアリング設定
async function setupPairingsAsHost(): Promise<{ player1: PlayerState; player2: PlayerState; firstPlayerId: string }[]> {
  if (!state.game) return [];

  const multiplayer = getMultiplayerManager();

  console.log('[Game] setupPairingsAsHost: players=', state.game.getState().players.map(p => p.info.name + '(' + p.info.id + ')'));

  // 奇数の場合はBot追加
  if (state.game.getState().players.length % 2 !== 0) {
    console.log('[Game] Adding bot for odd player count');
    state.game.fillWithBots();
  }

  // ペアリング生成
  const pairings = state.game.getMatchPairings();

  console.log('[Game] Generated pairings:', pairings.map((p, i) => ({
    index: i,
    p1: p.player1.info.name + '(' + p.player1.info.id + ')',
    p2: p.player2.info.name + '(' + p.player2.info.id + ')',
  })));

  // サーバーに保存するペアリング情報
  const pairingsSync = pairings.map((p, index) => ({
    player1Id: p.player1.info.id,
    player2Id: p.player2.info.id,
    firstPlayerId: p.firstPlayerId,
    matchIndex: index,
  }));

  // Bot情報
  const bots = state.game.getState().players
    .filter(p => p.info.type === 'bot')
    .map(p => ({
      id: p.info.id,
      name: p.info.name,
      difficulty: p.info.botDifficulty || 'normal',
    }));

  // サーバーに保存
  console.log('[Game] Saving pairings to server:', pairingsSync);
  await multiplayer.setPairings(pairingsSync, bots);

  // 非ホストプレイヤーがペアリングを受け取るまで待機
  const humanCount = state.game.getState().players.filter(p => p.info.type === 'human').length;
  if (humanCount > 1) {
    console.log('[Game] Host waiting for other players to receive pairings...');
    await multiplayer.waitForPairingsAck(humanCount - 1, 10000);
    console.log('[Game] All players received pairings');
  }

  return pairings;
}

// 非ホスト: ペアリング受信
async function receivePairingsFromHost(): Promise<{ player1: PlayerState; player2: PlayerState; firstPlayerId: string }[]> {
  if (!state.game) return [];

  const multiplayer = getMultiplayerManager();

  console.log('[Game] receivePairingsFromHost: waiting for pairings...');

  // サーバーからペアリング情報を取得
  const result = await multiplayer.waitForPairings(15000);

  if (!result) {
    console.error('[Game] Failed to receive pairings from host');
    return [];
  }

  console.log('[Game] Received pairings from server:', result.pairings);
  console.log('[Game] Received bots from server:', result.bots);

  // ペアリング受信完了を通知 (ホストに知らせる)
  await multiplayer.notifyPairingsAck();
  console.log('[Game] Notified pairings acknowledgment');

  // ホストが全員のackを確認して準備完了するまで少し待つ
  // (ホストがwaitForPairingsAckを完了してshowMultiplayerBattleに入る時間を確保)
  console.log('[Game] Waiting for host to complete pairing sync...');
  await new Promise(resolve => setTimeout(resolve, 1000));

  // Bot追加 (ホストと同じBotを追加)
  for (const botSync of result.bots) {
    const existing = state.game.getPlayer(botSync.id);
    if (!existing) {
      console.log('[Game] Adding bot from host:', botSync.name);
      state.game.addBotFromSync(botSync.id, botSync.name, botSync.difficulty);
    }
  }

  // ペアリング情報からローカルのペアリングオブジェクトを構築
  const pairings: { player1: PlayerState; player2: PlayerState; firstPlayerId: string }[] = [];

  for (const pairingSync of result.pairings) {
    const player1 = state.game.getPlayer(pairingSync.player1Id);
    const player2 = state.game.getPlayer(pairingSync.player2Id);

    if (player1 && player2) {
      pairings.push({
        player1,
        player2,
        firstPlayerId: pairingSync.firstPlayerId,
      });
    } else {
      console.warn('[Game] Player not found for pairing:', pairingSync);
    }
  }

  return pairings;
}

// マルチプレイ対人戦のバトル表示
async function showMultiplayerBattle(
  pairing: { player1: PlayerState; player2: PlayerState; firstPlayerId: string },
  round: number,
  opponentIsBot: boolean,
  matchIndex: number = 0  // マッチのインデックス (複数マッチ対応)
): Promise<void> {
  if (!state.game || !state.currentUser) return;

  const multiplayer = getMultiplayerManager();
  
  // 対戦相手のID
  const opponentId = pairing.player1.info.id === state.currentUser.id
    ? pairing.player2.info.id
    : pairing.player1.info.id;
  
  // このマッチの作成担当者を決定 (IDが小さい方が担当)
  // これによりラウンドごとにplayer1/player2の順番が変わっても担当者は固定される
  const isMatchCreator = state.currentUser.id < opponentId;
  
  console.log('[Game] showMultiplayerBattle: matchIndex=', matchIndex, 'isMatchCreator=', isMatchCreator, 'myId=', state.currentUser.id, 'opponentId=', opponentId);

  // 待機画面を表示
  render(`
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: #1a1a2e;
      color: #fff;
    ">
      <div style="font-size: 3rem; margin-bottom: 20px;">⚔️</div>
      <h2 style="color: #ff6b6b; margin-bottom: 10px;">対戦準備中...</h2>
      <p style="color: #aaa;">${isMatchCreator ? 'マッチを初期化しています' : '相手のマッチ情報を待っています'}</p>
    </div>
  `);

  let matchPhase: MatchPhase;

  if (isMatchCreator) {
    // マッチ作成担当: MatchPhaseを作成してサーバーに保存
    const result = await initMultiplayerMatch(
      pairing.player1.info.id,
      [...pairing.player1.deck],
      pairing.player2.info.id,
      [...pairing.player2.deck],
      pairing.firstPlayerId,
      round,
      true,
      matchIndex,
      state.currentUser.id,
      opponentId
    );

    if (!result) {
      console.error('[Game] Failed to create multiplayer match');
      return;
    }

    matchPhase = result.matchPhase;
  } else {
    // 非担当: サーバーからマッチ状態を待機
    const result = await initMultiplayerMatch(
      pairing.player1.info.id,
      [...pairing.player1.deck],
      pairing.player2.info.id,
      [...pairing.player2.deck],
      pairing.firstPlayerId,
      round,
      false,
      matchIndex,
      state.currentUser.id,
      opponentId
    );

    if (!result) {
      console.error('[Game] Failed to get multiplayer match from server');
      // フォールバック: ローカルで作成
      const localMatch = state.game.createMatchPhase(
        pairing.player1.info.id,
        pairing.player2.info.id,
        pairing.firstPlayerId
      );
      if (!localMatch) return;
      matchPhase = localMatch;
    } else {
      matchPhase = result.matchPhase;
    }
  }

  // バトル画面を表示 (マルチプレイモード)
  renderMatchBattle(
    matchPhase, 
    state.currentUser.id, 
    opponentId, 
    opponentIsBot, 
    async (result) => {
      cleanupMatchBattle();
      
      // MatchPhaseから正式な結果を取得
      const matchResult = matchPhase.getResult();
      if (matchResult) {
        state.game?.registerMatchResult(matchResult);
        
        // マルチプレイ時はサーバーにも保存
        const currentRound = state.game?.getState().currentRound?.roundNumber ?? 1;
        await multiplayer.saveMatchResult({
          matchIndex,
          winnerId: matchResult.winnerId,
          loserId: matchResult.loserId,
          winnerFlags: matchResult.winnerFlags,
          round: currentRound,
        });
        console.log('[Game] Match result saved to server');
      }
      
      // 他のBot同士のマッチも実行
      runAllBotMatches();
      
      // マルチプレイ: 全マッチ完了を待機
      await waitForAllMatchesAndComplete();
    },
    true,  // isMultiplayer
    isMatchCreator,
    matchIndex
  );
}

// Bot同士のマッチを実行
function runAllBotMatches(): void {
  if (!state.game) return;

  const pairings = state.game.getMatchPairings();
  
  for (const pairing of pairings) {
    // 両方Botの場合のみ自動実行
    if (pairing.player1.info.type === 'bot' && pairing.player2.info.type === 'bot') {
      const matchPhase = state.game.createMatchPhase(
        pairing.player1.info.id,
        pairing.player2.info.id,
        pairing.firstPlayerId
      );
      
      if (matchPhase) {
        const result = matchPhase.runToCompletion();
        if (result) {
          state.game.registerMatchResult(result);
        }
      }
    }
  }
}

// マルチプレイ: 全マッチ完了を待機してラウンド終了
async function waitForAllMatchesAndComplete(): Promise<void> {
  if (!state.game || !state.isMultiplayer) {
    // シングルプレイの場合は即座にラウンド終了
    state.game?.completeMatches();
    return;
  }

  const multiplayer = getMultiplayerManager();
  const pairings = state.game.getMatchPairings();
  
  // 待機画面を表示
  render(`
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
      color: #fff;
    ">
      <div style="font-size: 3rem; margin-bottom: 20px;">⏳</div>
      <h2 style="color: #00fff5; margin-bottom: 10px;">他の対戦を待機中...</h2>
      <p style="color: #aaa;">全員の対戦が終了するまでお待ちください</p>
    </div>
  `);

  // サーバーにマッチ完了を通知
  const result = await multiplayer.notifyMatchComplete(pairings.length);
  console.log('[Game] Match complete notification result:', result);

  // 既に全員完了していたら即座に結果同期へ
  if (!result.allComplete) {
    // 全マッチ完了を待機 (最大10分)
    const allComplete = await multiplayer.waitForAllMatchesComplete(600000);
    
    if (allComplete) {
      console.log('[Game] All matches completed, syncing results');
    } else {
      console.warn('[Game] Timeout waiting for all matches, proceeding anyway');
    }
  } else {
    console.log('[Game] All matches already complete');
  }

  // サーバーから全マッチ結果を取得して反映
  await syncMatchResultsFromServer();

  // ラウンド終了
  state.game?.completeMatches();
}

// サーバーからマッチ結果を同期
async function syncMatchResultsFromServer(): Promise<void> {
  if (!state.game || !state.isMultiplayer) return;

  const multiplayer = getMultiplayerManager();
  const serverResults = await multiplayer.getMatchResults();
  
  console.log('[Game] Syncing match results from server:', serverResults.length, 'results');

  const currentRound = state.game.getState().currentRound;
  if (!currentRound) return;

  // 既に登録済みの結果をチェック
  const existingResultIds = new Set(
    currentRound.matchResults.map(r => `${r.winnerId}-${r.loserId}`)
  );

  for (const result of serverResults) {
    const resultId = `${result.winnerId}-${result.loserId}`;
    
    // まだ登録されていない結果のみ追加
    if (!existingResultIds.has(resultId)) {
      console.log('[Game] Adding match result from server:', result.winnerId, 'vs', result.loserId);
      
      // MatchResultを構築してregister
      state.game.registerMatchResult({
        matchId: `sync_${result.matchIndex}`,
        round: result.round,
        winnerId: result.winnerId,
        loserId: result.loserId,
        winnerFansGained: 0,  // 後でregisterMatchResult内で計算される
        winnerFlags: result.winnerFlags,
        finalState: null as any,  // 他プレイヤーのマッチ詳細は不要
      });
    }
  }
}

// マッチ結果表示 (レガシー、使われなくなる予定)
async function showMatchResults(): Promise<void> {
  if (!state.game || !state.currentUser) return;

  const currentRound = state.game.getState().currentRound;
  if (!currentRound) return;

  // 自分が参加したマッチを探す
  const myMatch = currentRound.matchResults.find(
    (r) => r.winnerId === state.currentUser?.id || r.loserId === state.currentUser?.id
  );

  if (myMatch) {
    const player = state.game.getPlayer(state.currentUser.id);
    if (!player) return;
    
    // ストリーク計算
    const streak = calculateStreak(player.matchHistory);
    
    await renderMatchPhase(
      myMatch.finalState, 
      state.currentUser.id, 
      myMatch,
      {
        onMatchComplete: (summary) => {
          console.log('Match complete:', summary);
          // ラウンド結果は roundEnded イベントで表示
        },
      },
      {
        currentFans: player.fans,
        streak,
        isEliminated: player.isEliminated,
      }
    );
  }
}

// ラウンド結果表示
function showRoundResult(): void {
  if (!state.game) return;

  const gameState = state.game.getState();
  const round = gameState.roundHistory.length > 0
    ? gameState.roundHistory[gameState.roundHistory.length - 1].roundNumber
    : 1;

  renderRoundResult(state.game, round, {
    onNext: () => {
      if (!state.game) return;
      
      const phase = state.game.getState().phase;
      if (phase === 'finals') {
        // 決勝進出者発表画面へ
        showFinalistsAnnouncement();
      } else if (phase === 'roundEnd') {
        // ラウンド間遷移画面へ
        showRoundTransitionScreen(round);
      } else if (phase === 'gameEnd') {
        showGameEnd();
      }
    },
  });
}

// ラウンド間遷移画面
function showRoundTransitionScreen(completedRound: number): void {
  if (!state.game) return;
  
  renderRoundTransition(state.game, completedRound, {
    onContinue: () => {
      if (!state.game) return;
      
      // 次ラウンドへ進行
      state.game.proceedToNextRound();
    },
  });
}

// 決勝進出者発表
function showFinalistsAnnouncement(): void {
  if (!state.game) return;
  
  const gameState = state.game.getState();
  const finalistIds = gameState.finalists;
  
  const finalists = finalistIds
    .map((id) => state.game?.getPlayer(id))
    .filter((p): p is PlayerState => p !== undefined);
  
  const allPlayers = gameState.players;
  
  renderFinalistsAnnouncement(finalists, allPlayers, {
    onContinue: () => {
      showFinals();
    },
  });
}

// 決勝表示
function showFinals(): void {
  if (!state.game) return;

  const gameState = state.game.getState();
  const finalists = gameState.finalists;

  const p1 = state.game.getPlayer(finalists[0]);
  const p2 = state.game.getPlayer(finalists[1]);

  if (p1 && p2) {
    renderFinalsIntro(p1, p2, () => {
      // 決勝戦実行
      state.game?.runFinalsMatch();
    });
  }
}

// ゲーム終了表示
function showGameEnd(): void {
  if (!state.game) return;

  const gameState = state.game.getState();
  const champion = state.game.getPlayer(gameState.champion ?? '');
  const ranking = state.game.getRanking();

  if (champion) {
    renderGameEnd(champion, ranking, {
      onNext: () => {},
      onRestart: init,
    });
  }
}

// 初期化
async function init(): Promise<void> {
  render('<div class="loading">Initializing...</div>');

  try {
    // Discord SDK初期化を試みる
    const discord = getDiscordClient();
    
    render('<div class="loading">Connecting to Discord...</div>');
    
    try {
      await discord.ready();
      
      render('<div class="loading">Authenticating...</div>');
      const auth = await discord.authorize();
      
      state.isDiscordReady = true;
      state.currentUser = {
        id: auth.user.id,
        name: auth.user.globalName || auth.user.username,
        avatar: discord.getAvatarUrl(auth.user),
      };

      console.log('Discord auth success:', state.currentUser);
    } catch (discordErr) {
      console.warn('Discord SDK error, falling back to local mode:', discordErr);
      throw discordErr; // ローカルモードへ
    }

  } catch (err) {
    console.warn('Discord SDK not available, using local mode:', err);
    
    // ローカルモードにフォールバック
    state.currentUser = {
      id: 'local_' + Date.now(),
      name: 'Player',
    };
  }

  // チュートリアル確認
  if (!isTutorialCompleted()) {
    showTutorial();
    return;
  }

  // ゲーム初期化へ
  initGame();
}

// チュートリアル表示
function showTutorial(): void {
  const app = document.getElementById('app');
  if (!app) return;

  renderTutorialScreen(app, () => {
    // チュートリアル完了後、ゲーム初期化
    initGame();
  });
}

// クイックヘルプ表示
function showQuickHelp(onClose: () => void): void {
  const helpContainer = document.createElement('div');
  helpContainer.id = 'help-overlay';
  document.body.appendChild(helpContainer);

  renderQuickHelp(helpContainer, () => {
    helpContainer.remove();
    state.showingHelp = false;
    onClose();
  });
  
  state.showingHelp = true;
}

// ゲーム初期化
function initGame(): void {
  // ゲームマネージャー初期化
  state.game = new GameManager();
  state.currentDeckPhase = null;

  // 自分をプレイヤーとして追加
  const myPlayer: PlayerState = {
    info: {
      id: state.currentUser!.id,
      name: state.currentUser!.name,
      type: 'human',
    },
    deck: [],
    bench: [],
    benchLimit: 6,
    fans: 0,
    matchHistory: [],
    isEliminated: false,
  };

  state.game.addPlayer(myPlayer);

  // マルチプレイ設定 (Discord Activity内の場合)
  if (state.isDiscordReady) {
    setupMultiplayer();
  }

  showLobby();
}

// マルチプレイ設定
function setupMultiplayer(): void {
  const multiplayer = getMultiplayerManager();
  
  multiplayer.setCallbacks({
    onPlayerJoin: (player) => {
      console.log('[Game] Player joined:', player);
      
      if (state.game) {
        // 既に存在するか確認
        const existing = state.game.getPlayer(player.id);
        if (!existing) {
          const newPlayer: PlayerState = {
            info: {
              id: player.id,
              name: player.name,
              type: 'human',
            },
            deck: [],
            bench: [],
            benchLimit: 6,
            fans: 0,
            matchHistory: [],
            isEliminated: false,
          };
          state.game.addPlayer(newPlayer);
          
          // ロビーフェーズの時だけ再描画
          if (state.game.getState().phase === 'lobby') {
            showLobby();
          }
        }
      }
    },
    onPlayerLeave: (playerId) => {
      console.log('[Game] Player left:', playerId);
      state.game?.removePlayer(playerId);
      
      // ロビーフェーズの時だけ再描画
      if (state.game?.getState().phase === 'lobby') {
        showLobby();
      }
    },
    onGameStart: () => {
      if (!multiplayer.getIsHost()) {
        startGame();
      }
    },
    onDeckPhaseComplete: (playerId) => {
      console.log('[Game] Player deck phase complete:', playerId);
      if (state.game && playerId !== state.currentUser?.id) {
        // 他プレイヤーの準備完了
        state.game.setPlayerReady(playerId, true);
        updateReadyStatus(state.game);
      }
    },
    onAllPlayersReady: () => {
      console.log('[Game] All players ready - proceeding to match phase');
      // GameManagerが自動的にマッチフェーズに進む
    },
    onRoomStateUpdate: (roomState) => {
      console.log('[Game] Room state update:', roomState);
      // サーバーのready状態をGameManagerに同期
      if (state.game) {
        for (const p of roomState.players) {
          // サーバー上でisReady=falseならGameManagerもリセット
          if (!p.isReady) {
            state.game.setPlayerReady(p.id, false);
          }
        }
        // UI更新
        updateReadyStatus(state.game);
      }
    },
  });

  // ルーム作成 (最初の人がホスト)
  multiplayer.createRoom().then(async (roomId) => {
    console.log('[Game] Room created/joined:', roomId);
    state.isMultiplayer = true;
    
    // 既存プレイヤーを取得してGameManagerに追加
    const roomState = await multiplayer.getRoomState();
    if (roomState && state.game) {
      console.log('[Game] Room has players:', roomState.players.length);
      for (const p of roomState.players) {
        if (p.id !== state.currentUser?.id) {
          const existing = state.game.getPlayer(p.id);
          if (!existing) {
            console.log('[Game] Adding existing player:', p.name);
            const newPlayer: PlayerState = {
              info: {
                id: p.id,
                name: p.name,
                type: 'human',
              },
              deck: [],
              bench: [],
              benchLimit: 6,
              fans: 0,
              matchHistory: [],
              isEliminated: false,
            };
            state.game.addPlayer(newPlayer);
          }
        }
      }
      // UI更新
      if (state.game.getState().phase === 'lobby') {
        showLobby();
      }
    }
    
    multiplayer.startListening();
  }).catch((err) => {
    console.error('[Game] Failed to create room:', err);
  });
}

// アプリ起動
init().catch((err) => {
  console.error('Init error:', err);
  render(`<div class="loading" style="color: #ff6b6b;">Error: ${err.message}</div>`);
});
